import { OrganizationType } from "../types/organization.type";

export type CanonicalRole = "admin" | "ops_manager" | "staff" | "admin_read_only";

export const WASTE_OUTLET_SITE_ROLES: readonly CanonicalRole[] = [
  "ops_manager",
  "staff",
  "admin_read_only",
];

export function wasteOutletRoleNeedsCollectionSites(role: CanonicalRole | ""): boolean {
  return role !== "" && (WASTE_OUTLET_SITE_ROLES as readonly string[]).includes(role);
}

export type RoleOrgType = OrganizationType.WASTE_OUTLET | OrganizationType.RECYCLER | OrganizationType.TRUCK;

export const CANONICAL_ROLE_LABELS: Record<CanonicalRole, string> = {
  admin: "Admin",
  ops_manager: "Operations manager",
  staff: "Staff",
  admin_read_only: "Read-only",
};

const ORG = "{organization_id}";
const SITE = "{collection_site_id}";

function siteScoped(org: string, site: string, entity: string, action: string): string {
  return `recyclee.${org}.${site}.${entity}.${action}`;
}

function siteWildcard(org: string, site: string, entity: string): string {
  return `recyclee.${org}.${site}.${entity}.*`;
}

function orgScoped(org: string, entity: string, action: string): string {
  return `recyclee.${org}.*.${entity}.${action}`;
}

function orgWildcard(org: string, entity: string): string {
  return `recyclee.${org}.*.${entity}.*`;
}

const WASTE_OUTLET_SITE_ENTITIES = [
  "pickup_order",
  "pre_order",
  "collection_site",
  "container",
  "material_type",
] as const;

function wasteOutletOpsManagerPerSite(): string[] {
  const perSite = WASTE_OUTLET_SITE_ENTITIES.flatMap((e) => [siteWildcard(ORG, SITE, e)]);
  const orgWide = [
    orgScoped(ORG, "dashboard", "read"),
    orgScoped(ORG, "esg_report", "read"),
    orgScoped(ORG, "approval", "submit"),
    orgScoped(ORG, "pickup_order", "reject"),
    orgScoped(ORG, "pickup_order", "rollback_after_pickup"),
    orgScoped(ORG, "training_program", "manage"),
    orgScoped(ORG, "training_record", "read"),
    orgWildcard(ORG, "invoice"),
    orgWildcard(ORG, "notification"),
    orgWildcard(ORG, "report"),
    orgScoped(ORG, "forms_submission", "create"),
    orgScoped(ORG, "upload", "create"),
  ];
  return [...perSite, ...orgWide];
}

function wasteOutletStaffPerSite(): string[] {
  const perSite = WASTE_OUTLET_SITE_ENTITIES.flatMap((e) => [
    siteScoped(ORG, SITE, e, "list"),
    siteScoped(ORG, SITE, e, "read"),
    siteScoped(ORG, SITE, e, "write"),
    siteScoped(ORG, SITE, e, "create"),
  ]);
  return [
    ...perSite,
    orgScoped(ORG, "notification", "list"),
    orgScoped(ORG, "training_record", "read"),
    orgScoped(ORG, "upload", "create"),
  ];
}

function wasteOutletReadOnlyPerSite(): string[] {
  const perSite = WASTE_OUTLET_SITE_ENTITIES.flatMap((e) => [
    siteScoped(ORG, SITE, e, "list"),
    siteScoped(ORG, SITE, e, "read"),
  ]);
  const orgWide = [
    orgScoped(ORG, "dashboard", "read"),
    orgScoped(ORG, "esg_report", "read"),
    orgScoped(ORG, "forms_submission", "list"),
    orgScoped(ORG, "forms_submission", "read"),
    orgScoped(ORG, "invoice", "list"),
    orgScoped(ORG, "invoice", "read"),
    orgScoped(ORG, "notification", "list"),
    orgScoped(ORG, "report", "list"),
    orgScoped(ORG, "report", "read"),
    orgScoped(ORG, "training_program", "list"),
    orgScoped(ORG, "training_record", "read"),
    orgScoped(ORG, "organization", "read"),
  ];
  return [...perSite, ...orgWide];
}

export const ROLE_TEMPLATES: Record<
  RoleOrgType,
  Record<CanonicalRole, readonly string[]>
> = {
  [OrganizationType.WASTE_OUTLET]: {
    admin: [
      `recyclee.${ORG}.*`,
      `recyclee.${ORG}.*.user.*`,
      `recyclee.${ORG}.*.role.*`,
      `recyclee.${ORG}.*.document.*`,
      `recyclee.${ORG}.*.collection_site.create`,
      `recyclee.${ORG}.*.collection_site.list`,
    ],
    ops_manager: wasteOutletOpsManagerPerSite(),
    staff: wasteOutletStaffPerSite(),
    admin_read_only: wasteOutletReadOnlyPerSite(),
  },
  [OrganizationType.RECYCLER]: {
    admin: [
      `recyclee.${ORG}.*`,
      `recyclee.${ORG}.*.user.*`,
      `recyclee.${ORG}.*.role.*`,
      `recyclee.${ORG}.*.document.*`,
      orgScoped(ORG, "dashboard", "read"),
      orgScoped(ORG, "esg_report", "read"),
      orgWildcard(ORG, "training_program"),
      orgScoped(ORG, "training_record", "read"),
      orgWildcard(ORG, "invoice"),
      orgWildcard(ORG, "notification"),
      orgWildcard(ORG, "report"),
      orgScoped(ORG, "forms_submission", "create"),
      orgScoped(ORG, "upload", "create"),
    ],
    ops_manager: [
      orgWildcard(ORG, "delivery"),
      orgWildcard(ORG, "purchase_order"),
      orgWildcard(ORG, "offer"),
      orgWildcard(ORG, "material_type"),
      orgWildcard(ORG, "appetite"),
      orgWildcard(ORG, "truck"),
      orgScoped(ORG, "driver", "list"),
      orgScoped(ORG, "driver", "read"),
      orgScoped(ORG, "dashboard", "read"),
      orgScoped(ORG, "esg_report", "read"),
      orgScoped(ORG, "training_program", "manage"),
      orgScoped(ORG, "training_record", "read"),
      orgWildcard(ORG, "invoice"),
      orgWildcard(ORG, "notification"),
      orgWildcard(ORG, "report"),
      orgScoped(ORG, "forms_submission", "create"),
      orgScoped(ORG, "upload", "create"),
    ],
    staff: [
      orgScoped(ORG, "delivery", "list"),
      orgScoped(ORG, "delivery", "read"),
      orgScoped(ORG, "material_type", "list"),
      orgScoped(ORG, "material_type", "read"),
      orgScoped(ORG, "purchase_order", "list"),
      orgScoped(ORG, "purchase_order", "read"),
      orgScoped(ORG, "report", "list"),
      orgScoped(ORG, "report", "read"),
      orgScoped(ORG, "notification", "list"),
      orgScoped(ORG, "training_record", "read"),
      orgScoped(ORG, "upload", "create"),
    ],
    admin_read_only: [
      orgScoped(ORG, "delivery", "list"),
      orgScoped(ORG, "delivery", "read"),
      orgScoped(ORG, "purchase_order", "list"),
      orgScoped(ORG, "purchase_order", "read"),
      orgScoped(ORG, "offer", "list"),
      orgScoped(ORG, "offer", "read"),
      orgScoped(ORG, "material_type", "list"),
      orgScoped(ORG, "material_type", "read"),
      orgScoped(ORG, "appetite", "list"),
      orgScoped(ORG, "appetite", "read"),
      orgScoped(ORG, "truck", "list"),
      orgScoped(ORG, "truck", "read"),
      orgScoped(ORG, "dashboard", "read"),
      orgScoped(ORG, "esg_report", "read"),
      orgScoped(ORG, "training_record", "read"),
      orgScoped(ORG, "organization", "read"),
    ],
  },
  [OrganizationType.TRUCK]: {
    admin: [
      `recyclee.${ORG}.*`,
      `recyclee.${ORG}.*.user.*`,
      `recyclee.${ORG}.*.role.*`,
      `recyclee.${ORG}.*.document.*`,
      orgScoped(ORG, "dashboard", "read"),
      orgWildcard(ORG, "training_program"),
      orgScoped(ORG, "training_record", "read"),
      orgWildcard(ORG, "notification"),
      orgScoped(ORG, "upload", "create"),
    ],
    ops_manager: [
      orgWildcard(ORG, "truck"),
      orgWildcard(ORG, "driver"),
      orgWildcard(ORG, "assignment"),
      orgScoped(ORG, "pickup_order", "list"),
      orgScoped(ORG, "pickup_order", "read"),
      orgScoped(ORG, "dashboard", "read"),
      orgScoped(ORG, "training_program", "manage"),
      orgScoped(ORG, "training_record", "read"),
      orgWildcard(ORG, "notification"),
      orgScoped(ORG, "upload", "create"),
    ],
    staff: [
      orgScoped(ORG, "assignment", "list"),
      orgScoped(ORG, "assignment", "read"),
      orgScoped(ORG, "assignment", "accept"),
      orgScoped(ORG, "assignment", "reject"),
      orgScoped(ORG, "pickup_order", "list"),
      orgScoped(ORG, "pickup_order", "read"),
      orgScoped(ORG, "notification", "list"),
      orgScoped(ORG, "training_record", "read"),
    ],
    admin_read_only: [
      orgScoped(ORG, "truck", "list"),
      orgScoped(ORG, "truck", "read"),
      orgScoped(ORG, "driver", "list"),
      orgScoped(ORG, "driver", "read"),
      orgScoped(ORG, "assignment", "list"),
      orgScoped(ORG, "assignment", "read"),
      orgScoped(ORG, "pickup_order", "list"),
      orgScoped(ORG, "pickup_order", "read"),
      orgScoped(ORG, "dashboard", "read"),
      orgScoped(ORG, "training_record", "read"),
      orgScoped(ORG, "organization", "read"),
    ],
  },
};

const LEGACY_ROLE_MAP: Record<RoleOrgType, Record<CanonicalRole, string>> = {
  [OrganizationType.WASTE_OUTLET]: {
    admin: "waste_outlet_admin",
    ops_manager: "waste_outlet_ops_manager",
    staff: "waste_outlet_staff",
    admin_read_only: "admin_read_only",
  },
  [OrganizationType.RECYCLER]: {
    admin: "recycler_admin",
    ops_manager: "recycler_ops_manager",
    staff: "recycler_staff",
    admin_read_only: "admin_read_only",
  },
  [OrganizationType.TRUCK]: {
    admin: "truck_co_admin",
    ops_manager: "truck_co_ops_manager",
    staff: "truck_co_staff",
    admin_read_only: "admin_read_only",
  },
};

const LEGACY_TO_CANONICAL: Record<string, CanonicalRole> = {
  admin: "admin",
  manager: "ops_manager",
  trucker: "staff",
  admin_read_only: "admin_read_only",
  waste_outlet_admin: "admin",
  waste_outlet_ops_manager: "ops_manager",
  waste_outlet_staff: "staff",
  recycler_admin: "admin",
  recycler_ops_manager: "ops_manager",
  recycler_staff: "staff",
  truck_co_admin: "admin",
  truck_co_ops_manager: "ops_manager",
  truck_co_staff: "staff",
};

export function isRoleOrgType(orgType: OrganizationType): orgType is RoleOrgType {
  return (
    orgType === OrganizationType.WASTE_OUTLET ||
    orgType === OrganizationType.RECYCLER ||
    orgType === OrganizationType.TRUCK
  );
}

export function substituteTemplatePlaceholders(
  pattern: string,
  orgId: string,
  siteId: string,
): string {
  return pattern
    .split("{organization_id}")
    .join(orgId)
    .split("{collection_site_id}")
    .join(siteId);
}

export function expandTemplate(
  orgType: RoleOrgType,
  role: CanonicalRole,
  orgId: string,
  siteId = "*",
): string[] {
  const patterns = ROLE_TEMPLATES[orgType][role];
  return patterns.map((p) => substituteTemplatePlaceholders(p, orgId, siteId));
}

export function expandRoleForSites(
  orgType: RoleOrgType,
  role: CanonicalRole,
  orgId: string,
  siteIds: string[] | "*",
): string[] {
  if (orgType !== OrganizationType.WASTE_OUTLET) {
    return expandTemplate(orgType, role, orgId, "*");
  }
  if (siteIds === "*" || (Array.isArray(siteIds) && siteIds.includes("*"))) {
    return expandTemplate(orgType, role, orgId, "*");
  }
  const seen = new Set<string>();
  for (const siteId of siteIds) {
    for (const p of expandTemplate(orgType, role, orgId, siteId)) {
      seen.add(p);
    }
  }
  return [...seen];
}

export function legacyRoleKey(orgType: RoleOrgType, role: CanonicalRole): string {
  return LEGACY_ROLE_MAP[orgType][role];
}

export function canonicalRoleOptions(): { value: CanonicalRole; label: string }[] {
  return (Object.keys(CANONICAL_ROLE_LABELS) as CanonicalRole[]).map((value) => ({
    value,
    label: CANONICAL_ROLE_LABELS[value],
  }));
}

export function legacyRoleToCanonical(legacyRole: string): CanonicalRole | null {
  return LEGACY_TO_CANONICAL[legacyRole] ?? null;
}

export function canonicalRoleLabel(role: CanonicalRole | "custom" | "unknown"): string {
  if (role === "custom") return "Custom";
  if (role === "unknown") return "Unknown";
  return CANONICAL_ROLE_LABELS[role];
}
